import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { H2, H3, P, UL, Note, TBD, LEGAL_ENTITY, PRODUCT, CONTACT_EMAIL, SECURITY_EMAIL } from './LegalLayout';

/** Current subprocessors. Keep this table honest — it is a representation to customers. */
const SUBPROCESSORS = [
  ['Amazon Web Services', 'Cloud infrastructure, AI processing (Amazon Bedrock) and transactional email (Amazon SES — link-only, no health information)', 'United States', 'Yes — BAA executed'],
  ['MongoDB Atlas', 'Database and document storage', 'United States', 'Yes — required before production PHI'],
  ['Render', 'Application hosting', 'United States', 'Yes — required before production PHI'],
  ['Stripe', 'Payment processing (no health information)', 'United States', 'Not required — no PHI shared'],
  ['Twilio', 'Text messages (link-only, no health information)', 'United States', 'Not required — no PHI shared'],
  ['Sinch', 'Fax delivery — transmits the document itself', 'United States', 'Yes — feature disabled until executed'],
];

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle={`How ${LEGAL_ENTITY} handles information in ${PRODUCT}.`}
      updated="October 22, 2026"
    >
      <Note>
        <strong>Two different roles, and the difference matters.</strong> For information about the practices and staff
        who use {PRODUCT}, we decide how that information is used and this Policy governs it. For{' '}
        <strong>patient health information</strong>, we are a <em>service provider to your medical practice</em> — the
        practice decides what happens to it, and we act only on its instructions. If you are a patient, see the{' '}
        <Link to="/hipaa" className="font-medium text-primary hover:underline">HIPAA &amp; Health Information</Link>{' '}
        page, which explains your rights and who to ask.
      </Note>

      <H2 id="scope">1. Scope</H2>
      <P>
        This Policy covers the {PRODUCT} website, the practice-facing application, and the patient-facing intake and
        document pages. It does not cover a medical practice's own privacy practices — each practice is a separate
        organization with its own Notice of Privacy Practices.
      </P>

      <H2 id="collect">2. Information we collect</H2>

      <H3>From practices and their staff</H3>
      <UL>
        <li><strong>Account information</strong> — name, work email address, role, practice name, specialty, and (optionally) phone number.</li>
        <li><strong>Authentication data</strong> — a hashed password, multi-factor authentication secrets and backup codes, session tokens, and a browser-binding identifier.</li>
        <li><strong>Billing information</strong> — subscription plan, seat count, billing contact, and invoice history. Card details are entered directly with our payment processor; we receive only a token, the last four digits, and the card brand.</li>
        <li><strong>Support and feedback</strong> — messages you send us.</li>
      </UL>

      <H3>From patients, on behalf of the practice</H3>
      <P>
        A patient reaches the Service through a single-use secure link sent by their practice. There is no patient
        account and no patient password. Information collected includes:
      </P>
      <UL>
        <li>name, date of birth, and contact details;</li>
        <li>employer name and, where the form requires it, employer or administrator contact details;</li>
        <li>health information the form requires — condition, symptoms, treatment, functional limitations, and expected dates or duration of leave;</li>
        <li>documents and photographs the patient uploads, such as an employer's form pages;</li>
        <li>a record of the patient's consent to share their answers with their care team, captured before the intake form can be submitted.</li>
      </UL>

      <H3>Automatically</H3>
      <UL>
        <li><strong>Log data</strong> — IP address, browser and device type, pages requested, timestamps, and error traces. Used for security, abuse prevention, rate limiting, and debugging.</li>
        <li><strong>Audit records</strong> — every access to, generation of, and disclosure of health information is logged with the acting user, action, and time. Patient actions are recorded without patient identifiers.</li>
      </UL>

      <H3>What we do not collect</H3>
      <UL>
        <li>We do not use advertising cookies, advertising pixels, or third-party analytics, and we do not run session-replay tools. A session-replay tool was previously present and was removed in July 2026.</li>
        <li>We do not buy personal information from data brokers.</li>
        <li>We do not knowingly collect information from anyone under 13 through the website. Health information about a minor patient may be submitted by a parent or guardian through their practice.</li>
      </UL>

      <H2 id="cookies">3. Cookies and local storage</H2>
      <P>
        We use only what the Service needs to function. There is no cookie consent banner because there is nothing
        optional to consent to.
      </P>
      <UL>
        <li><strong>Session token</strong> — stored in your browser's local storage to keep you signed in. Expires after 12 hours.</li>
        <li><strong>Session-binding cookie</strong> — an <code className="rounded bg-muted px-1 text-xs">httpOnly</code> cookie that ties your session token to your browser, so a stolen token cannot be replayed elsewhere.</li>
        <li><strong>Language preference</strong> — remembers English or Spanish on patient pages.</li>
      </UL>

      <H2 id="use">4. How we use information</H2>
      <UL>
        <li><strong>To provide the Service</strong> — assemble and deliver leave paperwork, route it for nurse review and physician signature, and notify the people involved.</li>
        <li><strong>To authenticate and secure accounts</strong> — sign-in, multi-factor authentication, lockout after failed attempts, rate limiting, and fraud prevention.</li>
        <li><strong>To take payment</strong> — subscriptions, patient document fees, and per-document platform charges.</li>
        <li><strong>To support you</strong> — respond to questions and investigate problems you report.</li>
        <li><strong>To improve the Service</strong> — using aggregated, de-identified usage data. We do not use patient health information to train models, ours or anyone else's.</li>
        <li><strong>To comply with law</strong> — including HIPAA audit and breach-notification obligations.</li>
      </UL>
      <P>
        Where we act as a business associate, we use patient health information only as the practice's Business
        Associate Agreement permits and as needed to provide the Service. We never sell personal information or health
        information, and we do not share it for cross-context behavioral advertising.
      </P>

      <H2 id="ai">5. Artificial intelligence and de-identification</H2>
      <P>
        The Service uses large language models to draft narrative form content, summarize patient answers, read
        uploaded employer form pages, and answer staff questions. Before any patient information leaves our systems
        for a model, it passes through a de-identification layer that removes HIPAA Safe Harbor identifiers:
      </P>
      <UL>
        <li>patient, employer, and practice names;</li>
        <li>email addresses, phone numbers, and Social Security numbers, including those embedded in free text;</li>
        <li>dates of birth and calendar dates — dates are converted to durations or week offsets, because an absolute date is itself an identifier.</li>
      </UL>
      <P>
        The model receives clinical context without identity: condition, procedure, severity, functional capacity, and
        durations. The complete record stays in our database; only the outbound request is de-identified.
      </P>
      <P>
        AI processing runs on Amazon Bedrock, under a Business Associate Agreement with Amazon Web Services executed
        on July 30, 2026, and restricted to HIPAA-eligible models. Model providers reached through Bedrock do not
        retain prompts to train their models.
      </P>

      <H2 id="share">6. Who we share information with</H2>
      <P>
        We share information with service providers who process it on our behalf, under contract, for the purposes
        below — and, where the data includes health information, under a Business Associate Agreement.
      </P>
      <div className="mt-4 overflow-x-auto rounded-xl border">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 font-medium">Provider</th>
              <th scope="col" className="px-3 py-2 font-medium">Purpose</th>
              <th scope="col" className="px-3 py-2 font-medium">Location</th>
              <th scope="col" className="px-3 py-2 font-medium">BAA</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {SUBPROCESSORS.map(([name, purpose, loc, baa]) => (
              <tr key={name} className="align-top">
                <th scope="row" className="px-3 py-2 font-medium text-foreground">{name}</th>
                <td className="px-3 py-2 text-muted-foreground">{purpose}</td>
                <td className="px-3 py-2 text-muted-foreground">{loc}</td>
                <td className="px-3 py-2 text-muted-foreground">{baa}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <P>
        We also disclose information when required by law or valid legal process, to protect our rights or someone's
        safety, and in connection with a merger or acquisition — in which case the acquirer remains bound by this
        Policy and by our Business Associate Agreements, and we will notify affected practices.
      </P>
      <P>
        Completed documents are disclosed to employers, insurers, and third-party administrators only at the direction
        of the practice and the patient.
      </P>

      <H2 id="communications">7. Email, text messages, and fax</H2>
      <P>
        <strong>Our emails and text messages never contain health information.</strong> They say that a document is
        ready and provide a secure link, so an intercepted message discloses nothing clinical. This is a deliberate
        design constraint, and it is why our email and SMS providers do not need a Business Associate Agreement.
      </P>
      <P>
        Fax is the exception: it transmits the completed document itself. It is therefore disabled by default and
        cannot be enabled until a Business Associate Agreement with the fax vendor is in place.
      </P>
      <P>
        Patients receive only transactional messages about their own paperwork. To stop text messages, reply{' '}
        <strong>STOP</strong>; message and data rates may apply. We do not send marketing texts.
      </P>

      <H2 id="security">8. How we protect information</H2>
      <UL>
        <li>Encryption in transit using TLS on every connection.</li>
        <li>Encryption at rest for the production database and file storage. The production database and stored files run on a dedicated MongoDB Atlas cluster with storage encryption at rest (AES-256).</li>
        <li>Stored payment-provider secrets are encrypted with a separate application key.</li>
        <li>Role-based access control enforced on the server for every request, with practice-level data isolation on every query.</li>
        <li>Unique accounts per person, bcrypt-hashed passwords, required authenticator-app multi-factor authentication, 12-hour session expiry, lockout after five failed sign-ins in fifteen minutes, and immediate revocation of all sessions on password change.</li>
        <li>Audit logging of access to and disclosure of health information, viewable by practice administrators.</li>
        <li>Rate limiting on public and authentication endpoints; security headers including frame-denial and referrer restriction.</li>
        <li>Patient links are single-purpose and time-limited: seven days before payment, ninety days after, then they stop working.</li>
      </UL>
      <P>
        No system is perfectly secure. If we discover a breach of unsecured health information we will notify the
        affected practice without unreasonable delay and within the timeframes HIPAA requires, and the practice — as
        the covered entity — notifies its patients.
      </P>

      <H2 id="retention">9. How long we keep information</H2>
      <P>
        A practice's records remain available to it for as long as its account is active, because medical leave
        documentation is part of the patient's medical record and practices are subject to their own retention
        obligations under state law.
      </P>
      <UL>
        <li><strong>Patient access links</strong> expire automatically — 7 days before payment, 90 days after.</li>
        <li><strong>Signed documents and their audit trail</strong> are retained for the life of the account, then handled as the Business Associate Agreement directs on termination.</li>
        <li><strong>Account and billing records</strong> are retained as long as needed for tax, accounting, and legal purposes.</li>
        <li><strong>Log data</strong> is retained for six (6) years.</li>
        <li><strong>Audit records</strong> are retained for six (6) years.</li>
      </UL>
      <Note>
        A formal data retention and disposal policy is still being finalized with counsel. Until it is published, we
        retain data rather than delete it, and honor a practice's written deletion instruction as its Business
        Associate Agreement requires.
      </Note>

      <H2 id="rights">10. Your choices and rights</H2>

      <H3>If you are a patient</H3>
      <P>
        Your rights in your health information — to see it, get a copy, request a correction, ask who it has been
        disclosed to, and restrict certain disclosures — are exercised through <strong>your medical practice</strong>,
        not through us. We hold your information on their behalf and will act on their instruction. If you contact us
        directly we will refer you to your practice, and tell them you asked. See the{' '}
        <Link to="/hipaa" className="font-medium text-primary hover:underline">HIPAA &amp; Health Information</Link>{' '}
        page.
      </P>

      <H3>If you are practice staff</H3>
      <UL>
        <li>Update your name, email, and password in your profile, or ask your practice administrator.</li>
        <li>Enable or reset multi-factor authentication, and sign out of all sessions everywhere.</li>
        <li>Ask us to delete your account information once your practice confirms you no longer require access. Records of actions you took in the audit log are retained — deleting them would defeat the purpose of an audit log and HIPAA requires we keep it.</li>
      </UL>

      <H3>State privacy laws</H3>
      <P>
        Residents of California, Colorado, Connecticut, Texas, Virginia, and other states with comprehensive privacy
        laws have rights to access, correct, delete, and obtain a portable copy of personal information, and to appeal
        a refusal. Two limits apply here:
      </P>
      <UL>
        <li>Protected health information held by a covered entity or business associate under HIPAA is exempt from those laws, so requests about patient health information go through the practice.</li>
        <li>For personal information not covered by that exemption, contact us at <span className="font-medium text-foreground">{CONTACT_EMAIL}</span>. We will verify your identity before acting and respond within the statutory period. We do not discriminate against anyone for exercising these rights.</li>
      </UL>
      <P>
        We do not sell personal information or share it for cross-context behavioral advertising, so there is nothing
        to opt out of. We do not process sensitive personal information for purposes requiring a separate opt-out.{' '}
        <TBD>Counsel to confirm whether an authorized-agent process and a "Your Privacy Choices" link are required
        given no sale or sharing occurs</TBD>
      </P>

      <H2 id="international">11. Where information is processed</H2>
      <P>
        The Service and all subprocessors listed above operate in the United States, and information is stored and
        processed there. The Service is offered to medical practices in the United States only, and is not directed to
        individuals in the European Economic Area, the United Kingdom, or Switzerland.
      </P>

      <H2 id="changes">12. Changes to this Policy</H2>
      <P>
        We will update this Policy as the Service changes. Material changes will be notified to practice
        administrators by email and posted here with a new "Last updated" date. We will not apply a materially
        different use to information already collected without a lawful basis or your consent.
      </P>

      <H2 id="contact">13. Contact us</H2>
      <P>
        Privacy questions and rights requests: <span className="font-medium text-foreground">{CONTACT_EMAIL}</span>
        <br />
        Security reports: <span className="font-medium text-foreground">{SECURITY_EMAIL}</span>
        <br />
        {LEGAL_ENTITY} · 530 Berwick Town, San Antonio, TX 78249
        <br />
        Privacy contact: Colin Harris, Privacy and Security Officer
      </P>
      <P>
        If you are a patient and believe your privacy rights have been violated, you may complain to your practice, to
        us, or to the U.S. Department of Health and Human Services Office for Civil Rights. No one may retaliate
        against you for filing a complaint.
      </P>
    </LegalLayout>
  );
}
